import { createFileRoute, notFound } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { buildProviderSlug } from "@/lib/provider-slug";
import {
 getProviderDirectoryPage,
 getProviderTotalCount,
 PROVIDERS_PER_DIRECTORY_PAGE,
} from "@/lib/seo.functions";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/doctors/directory/$page")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const page = Number(params.page);
 if (!Number.isInteger(page) || page < 0) throw notFound();
 const [{ total }, { providers }] = await Promise.all([
 getProviderTotalCount(),
 getProviderDirectoryPage({ data: { page } }),
 ]);
 if (providers.length === 0) throw notFound();
 return { page, total, providers };
 },
 head: ({ loaderData }) => {
 const page = loaderData?.page ?? 0;
 const total = loaderData?.total ?? 0;
 const providers = loaderData?.providers ?? [];
 const totalPages = total > 0 ? Math.ceil(total / PROVIDERS_PER_DIRECTORY_PAGE) : 0;

 const url = `${SITE}/doctors/directory/${page}`;
 const title =
 page === 0
 ? "Healthcare Provider Directory | Heallexa"
 : `Healthcare Provider Directory \u2014 Page ${page} | Heallexa`;
 const description =
 total > 0
 ? `Browse ${total.toLocaleString()} healthcare provider profiles on Heallexa \u2014 page ${page + 1} of ${totalPages}.`
 : `Browse healthcare provider profiles on Heallexa, page ${page}.`;

 const breadcrumbs = {
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 {
 "@type": "ListItem",
 position: 2,
 name: "Healthcare Provider Directory",
 item: `${SITE}/doctors/directory`,
 },
 ...(page > 0
 ? [{ "@type": "ListItem", position: 3, name: `Page ${page}`, item: url }]
 : []),
 ],
 };

 const itemList =
 providers.length > 0
 ? {
 "@context": "https://schema.org",
 "@type": "ItemList",
 name: title,
 numberOfItems: providers.length,
 itemListElement: (providers as any[]).map((p, i) => ({
 "@type": "ListItem",
 position: page * PROVIDERS_PER_DIRECTORY_PAGE + i + 1,
 name: formatProviderLabel(p),
 url: `${SITE}/providers/${buildProviderSlug(p)}`,
 })),
 }
 : null;

 const links: Array<{ rel: string; href: string }> = [
 { rel: "canonical", href: url },
 ];
 if (page > 0)
 links.push({ rel: "prev", href: `${SITE}/doctors/directory/${page - 1}` });
 if (totalPages > 0 && page < totalPages - 1)
 links.push({ rel: "next", href: `${SITE}/doctors/directory/${page + 1}` });

 return {
 meta: [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "website" },
 ],
 links,
 scripts: [
 { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
 ...(itemList
 ? [{ type: "application/ld+json", children: JSON.stringify(itemList) }]
 : []),
 ],
 };
 },
 errorComponent: DirectoryError,
 notFoundComponent: DirectoryNotFound,
 component: DoctorsDirectoryPage,
});

function DirectoryError({ error, reset }: { error: Error; reset: () => void }) {
 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-2xl px-6 py-24 text-center">
 <h1 className="font-display text-2xl font-semibold tracking-tight">This directory page is temporarily unavailable</h1>
 <p className="mt-3 text-sm text-muted-foreground">{error?.message ?? "Please try again in a moment."}</p>
 <button onClick={() => reset()} className="mt-6 inline-flex items-center justify-center h-10 px-5 text-[13px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md">Try again</button>
 <p className="mt-6 text-sm"><a href="/doctors/directory" className="text-primary hover:underline">← All directory pages</a></p>
 </main>
 <FooterSection />
 </div>
 );
}

function DirectoryNotFound() {
 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-2xl px-6 py-24 text-center">
 <h1 className="font-display text-2xl font-semibold tracking-tight">Directory page not found</h1>
 <p className="mt-3 text-sm text-muted-foreground">This directory page doesn't exist.</p>
 <p className="mt-6 text-sm"><a href="/doctors/directory" className="text-primary hover:underline">← All directory pages</a></p>
 </main>
 <FooterSection />
 </div>
 );
}

function DoctorsDirectoryPage() {
 const { page, total, providers } = Route.useLoaderData();
 const totalPages = Math.ceil(total / PROVIDERS_PER_DIRECTORY_PAGE);
 const last = totalPages - 1;

 // Build a small window of page numbers around the current one.
 const windowSize = 7;
 const start = Math.max(0, page - Math.floor(windowSize / 2));
 const end = Math.min(last, start + windowSize - 1);
 const windowPages: number[] = [];
 for (let i = start; i <= end; i++) windowPages.push(i);

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
 <nav className="text-sm text-muted-foreground mb-6">
 <a href="/sitemap" className="hover:text-primary transition-colors">Sitemap</a>
 <span className="mx-2">/</span>
 <a href="/specialties" className="hover:text-primary transition-colors">Specialties</a>
 <span className="mx-2">/</span>
 <a href="/locations" className="hover:text-primary transition-colors">Locations</a>
 <span className="mx-2">/</span>
 <a href="/insurance" className="hover:text-primary transition-colors">Insurance</a>
 <span className="mx-2">/</span>
 <a href="/doctors/directory" className="hover:text-primary transition-colors">Doctors</a>
 <span className="mx-2">/</span>
 <span className="text-foreground">Page {page}</span>
 </nav>

 <header className="mb-8">
 <p className="text-[12px] uppercase tracking-[0.14em] text-muted-foreground tabular-nums">
 Page {page.toLocaleString()} of {totalPages.toLocaleString()}
 </p>
 <h1 className="font-display text-[28px] font-semibold tracking-tight text-foreground mt-2">
 Healthcare Provider Directory
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground">
 Page {page.toLocaleString()} of {totalPages.toLocaleString()}. Browse healthcare
 provider profiles on Heallexa.
 </p>
 </header>

 <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mb-12">
 {providers.map((p: any) => {
 const slug = buildProviderSlug(p);
 const label = formatProviderLabel(p);
 return (
 <li key={p.id} className="flex items-center gap-2 text-[14px] min-w-0">
 <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
 <span className="truncate">
 <a
 href={`/providers/${slug}`}
 className="text-muted-foreground hover:text-primary transition-colors"
 >
 {label}
 </a>
 {p.city && p.state ? (
 <span className="text-muted-foreground/70">, {p.city}, {p.state}</span>
 ) : null}
 </span>
 </li>
 );
 })}
 </ul>

 <Pagination page={page} last={last} windowPages={windowPages} />

 <div className="mt-12 pt-8 border-t border-border text-[14px] text-muted-foreground space-x-4">
 <a href="/sitemap" className="hover:text-primary transition-colors">← Sitemap</a>
 <a href="/doctors/directory" className="hover:text-primary transition-colors">All directory pages</a>
 </div>
 </main>
 <FooterSection />
 </div>
 );
}

function formatProviderLabel(p: {
 name: string | null;
 credentials?: string | null;
 practice?: string | null;
 specialty?: string | null;
}) {
 const name = (p.name || "").trim();
 const creds = (p.credentials || "").trim();
 if (name && creds) return `${name}, ${creds}`;
 if (name) return name;
 if (p.practice) return p.practice;
 return p.specialty || "Healthcare provider";
}

function Pagination({
 page,
 last,
 windowPages,
}: {
 page: number;
 last: number;
 windowPages: number[];
}) {
 return (
 <nav className="flex flex-wrap items-center justify-center gap-2" aria-label="Directory pagination">
 <PageLink href="/doctors/directory/0" disabled={page === 0} label="First" />
 <PageLink
 href={`/doctors/directory/${Math.max(0, page - 1)}`}
 disabled={page === 0}
 label="Previous"
 />
 {windowPages.map((n) => (
 <a
 key={n}
 href={`/doctors/directory/${n}`}
 aria-current={n === page ? "page" : undefined}
 className={
 n === page
 ? "inline-flex items-center h-9 px-3 text-[13px] tabular-nums rounded-md border border-primary bg-primary text-primary-foreground font-medium"
 : "inline-flex items-center h-9 px-3 text-[13px] tabular-nums rounded-md border border-border bg-background hover:bg-muted transition-colors"
 }
 >
 {n}
 </a>
 ))}
 <PageLink
 href={`/doctors/directory/${Math.min(last, page + 1)}`}
 disabled={page === last}
 label="Next"
 />
 <PageLink href={`/doctors/directory/${last}`} disabled={page === last} label={`Last ${last}`} />
 </nav>
 );
}

function PageLink({ href, disabled, label }: { href: string; disabled: boolean; label: string }) {
 if (disabled) {
 return (
 <span className="inline-flex items-center h-9 px-3 text-[13px] rounded-md border border-border opacity-40 cursor-not-allowed">
 {label}
 </span>
 );
 }
 return (
 <a
 href={href}
 className="inline-flex items-center h-9 px-3 text-[13px] rounded-md border border-border bg-background hover:bg-muted transition-colors"
 >
 {label}
 </a>
 );
}
